import * as React from 'react';
import * as _ from 'lodash';
import { Dispatch, connect } from "react-redux";
import { AppActionCreators, IApplicationState, ReportActionCreators } from '@nkr/store/index';
import { ISwapFailureAnalysis } from '@nkr/models/index';
import { ISwapFailureAnalysisResponse } from '@nkr/store/reports';
import { AgGridReact } from 'ag-grid-react';
import { IHeaderParams, GridApi, GridReadyEvent, AgGridEvent, ICellRendererParams, ValueGetterParams, FirstDataRenderedEvent, RowDataChangedEvent, CsvExportParams, ProcessCellForExportParams, ColDef } from 'ag-grid-community';
import { ReportWrapper } from './styles';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import { Button, FormControl } from 'react-bootstrap';
import { Utility } from '@nkr/services/utility';

interface IOwnProps {
    isCenterLogin: boolean
}

interface IStateProps { }

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getSwapFailureAnalysis: (minimumTxp: number, failureCutoff: number) => Promise<ISwapFailureAnalysisResponse>
}

type TProps = IStateProps & IDispatchProps & IOwnProps

interface IState {
    data: ISwapFailureAnalysis[],
    centerId: number,
    group: string,
    minimumTxp: string,
    failureCutoff: string,
    gridApi: GridApi,
    columns: ColDef[]
}

class SwapFailureAnalysis extends React.Component<TProps, IState> {
    readonly state: IState = {
        data: [],
        centerId: 0,
        group: '',       
        minimumTxp: '4',
        failureCutoff: '20',
        gridApi: null,
        columns: [
            {
                colId: 'rank',
                headerName: 'Rank',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>R#</SortableHeaderComponent>,
                width: 50,
                sortable: false,
                valueGetter: (props: ValueGetterParams) =>  props.node.rowIndex + 1
            },

            {
                colId: 'center',
                field: 'centerAlias',
                headerName: 'Center',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Center</SortableHeaderComponent>,
                width: 155
            },

            {
                colId: 'transplants',
                field: 'totalTransplants',
                headerName: 'Transplants',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Transplants</SortableHeaderComponent>,
                width: 155
            },

            {
                colId: 'failures',
                field: 'failures',
                headerName: 'Swap Failures',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Swap Failures</SortableHeaderComponent>,
                width: 155
            },

            {
                colId: 'swapFailureRate',
                field: 'swapFailureRate',
                headerName: 'Center',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Swap Failure Rate</SortableHeaderComponent>,
                width: 155,
                cellRendererFramework: (props: ICellRendererParams) => {
                    let data = props.data as ISwapFailureAnalysis;

                    if (data.swapFailureRate == null) {
                        return <div>NV</div>;
                    }

                    return <div>{props.value}%</div>;
                }
            },

            {
                colId: 'rank',
                field: 'rank',
                headerName: 'Network Rank',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Rank In Network</SortableHeaderComponent>,
                width: 205,
                cellRendererFramework: (props: ICellRendererParams) => {
                    let data = props.data as ISwapFailureAnalysis;

                    if (data.category == 'LV') {
                        return (
                            <div>LV</div>
                        );
                    }

                    if (data.category == 'NV') {
                        return (
                            <div>NV</div>
                        );
                    }

                    return <div>{props.value}</div>;
                }
            },            
        ]
    }

    onGridReady = (e: GridReadyEvent) => {
        this.setState({
            gridApi: e.api
        });
    }

    onSortChanged(e: AgGridEvent) {
        e.api.refreshCells();
    }

    onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    }

    onRowDataChanged = (e: RowDataChangedEvent) => {
        e.api.sizeColumnsToFit();
    }

    componentDidMount = () => {
        this.getData('Getting swap failure analysis, one moment please...');
    }

    getData = (message: string) => {
        let { minimumTxp, failureCutoff } = this.state;

        this.props.showLoading(message).then(() => {
            this.props.getSwapFailureAnalysis(parseInt(minimumTxp), parseFloat(failureCutoff)).then(response => {
                this.setState({
                    data: response.data.results
                }, () => {
                    this.props.hideLoading();
                });
            });
        });
    }

    handleCenterChange = (e: React.FormEvent<FormControl & HTMLSelectElement>) => {
        this.setState({
            centerId: parseInt(e.currentTarget.value)
        });
    }

    handleMinimumTxpChange = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        this.setState({
            minimumTxp: e.currentTarget.value
        });
    }

    handleFailureCutoffChange = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        this.setState({
            failureCutoff: e.currentTarget.value
        });
    }

    handleSubmit = () => {
        this.getData('Updating VXM analysis, one moment please...');
    }


    handleExport = () => {
        let params: CsvExportParams = {
            columnGroups: true,
            fileName: 'swap-failure-analysis.csv',
            processCellCallback: (params: ProcessCellForExportParams) => {

                return params.value;
            }
        };

        this.state.gridApi.exportDataAsCsv(params);
    }

    render = () => {
        const filteredData = this.state.data.filter(x => {
            let center = this.state.centerId > 0 ? x.centerId == this.state.centerId : true;
            let group = this.state.group != '' ? x.category == this.state.group : true;

            return center && group;
        });

        const centerList = _.sortBy(this.state.data.map(item => {
            return { id: item.centerId, alias: item.centerAlias };
        }), item => item.alias);

        const groupList = [
            { id: 1, name: 'Green' },
            { id: 2, name: 'LV' },
            { id: 3, name: 'NV' },
            { id: 4, name: 'Red' },
        ];

        const { minimumTxp, failureCutoff } = this.state;

        const enableSubmit = Utility.isNumber(minimumTxp) && Utility.isNumber(failureCutoff);

        return (
            <>
                {this.props.isCenterLogin == false && (
                    <>
                        <div className='row'>
                            <div className="col-xs-11">
                                <div style={{ fontSize: 28, fontWeight: 500, marginBottom: 20 }}>Swap Failure Analysis (Last 12 Months)</div>
                            </div>
                        </div>

                        <div className='well well-sm' style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 14 }}>
                            <div style={{ display: 'flex' }}>
                                <div style={{ display: 'flex', alignItems: 'center', marginRight: 20 }}>
                                    <div style={{ marginRight: 5 }}>Minimum Txp Count:</div>
                                    <div>
                                        <FormControl
                                            style={{ width: 65 }}
                                            value={this.state.minimumTxp}
                                            onChange={this.handleMinimumTxpChange}
                                        />
                                    </div>
                                </div>

                                <div style={{ display: 'flex', alignItems: 'center', marginRight: 20 }}>
                                    <div style={{ marginRight: 5 }}>Failure Cutoff (%):</div>
                                    <div>
                                        <FormControl
                                            style={{ width: 65 }}
                                            value={this.state.failureCutoff}
                                            onChange={this.handleFailureCutoffChange}
                                        />
                                    </div>
                                </div>

                                <Button bsStyle='primary' disabled={!enableSubmit} onClick={this.handleSubmit}>Refresh Report</Button>
                            </div>

                            <div style={{ display: 'flex' }}>
                                <div style={{ display: 'flex', alignItems: 'center', marginRight: 20 }}>
                                    <div style={{ marginRight: 5 }}>Center:</div>
                                    <div>
                                        <FormControl
                                            componentClass='select'
                                            value={this.state.centerId}
                                            onChange={this.handleCenterChange}>
                                            <option value={0}>- All Centers -</option>
                                            {centerList.map(center =>
                                                <option key={`ctr-${center.id}`} value={center.id}>{center.alias}</option>
                                            )}
                                        </FormControl>
                                    </div>
                                </div>                              
                                <Button onClick={this.handleExport}>Export CSV</Button>
                            </div>
                        </div>
                    </>
                )}

                <ReportWrapper className='ag-theme-bootstrap'>
                    <AgGridReact
                        defaultColDef={{
                            headerClass: 'list-header',
                            cellClass: 'list-cell',
                            sortable: true,
                            resizable: false
                        }}
                        columnDefs={this.state.columns}
                        rowData={filteredData}
                        headerHeight={30}
                        groupHeaderHeight={30}
                        rowHeight={30}
                        suppressRowClickSelection={false}
                        suppressCellSelection={true}
                        noRowsOverlayComponentFramework={() => <div>No data for the selected filter ...</div>}
                        onGridReady={this.onGridReady}     
                        onSortChanged={this.onSortChanged}                   
                        onFirstDataRendered={this.onFirstDataRendered}
                        onRowDataChanged={this.onRowDataChanged}                        
                    />
                </ReportWrapper>
            </>
        );
    }
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {
        reports: appState.reports
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getSwapFailureAnalysis: (minimumTxp, failureCutoff) => {
            return dispatch(ReportActionCreators.getSwapFailureAnalysis(minimumTxp, failureCutoff));
        }
    };
}

export default connect<IStateProps, IDispatchProps, IOwnProps>(
    mapStateToProps,
    mapDispatchToProps)(SwapFailureAnalysis);